import React, { useCallback, useEffect, useState } from "react";
import {
  adminListOrgs,
  adminCreateOrg,
  adminUpdateOrg,
  adminListOrgBranches,
  adminAttachBranch,
  adminDetachBranch,
  adminUploadOrgLogo,
  adminUploadOrgCover,
  fetchAdminTenants,
  resolveImageUrl,
} from "../services/api";

export default function AdminGroupsPage() {
  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [branches, setBranches] = useState({});
  const [attachSel, setAttachSel] = useState({});
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", logo_url: "", cover_image_url: "", subscription_tier: "hotel", merge_branch_items: false });
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploading, setUploading] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [o, t] = await Promise.all([
        adminListOrgs(),
        fetchAdminTenants({ limit: 200 }),
      ]);
      setOrgs(o);
      setTenants((t && t.items) || []);
    } catch (e) {
      setErr("Could not load groups.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!form.name || !form.email || (form.password || "").length < 8) {
      setErr("Name, email, and a password of at least 8 characters are required.");
      return;
    }
    setCreating(true);
    setErr("");
    try {
      await adminCreateOrg({ ...form, subscription_tier: "hotel" });
      setMsg(`Group "${form.name}" created. Share the owner login (${form.email} + the password you set) with the group owner. They sign in at /owner/login.`);
      setForm({ name: "", email: "", password: "" });
      await load();
    } catch (e2) {
      setErr((e2 && e2.response && e2.response.data && e2.response.data.detail) || "Could not create group.");
    } finally {
      setCreating(false);
    }
  };

  const loadBranches = async (orgId) => {
    setBranches((b) => ({ ...b, [orgId]: undefined }));
    try {
      const list = await adminListOrgBranches(orgId);
      setBranches((b) => ({ ...b, [orgId]: list }));
    } catch (e) {
      setErr("Could not load the group's branches.");
    }
  };

  const toggle = (orgId) => {
    if (openId === orgId) {
      setOpenId(null);
      return;
    }
    setOpenId(orgId);
    loadBranches(orgId);
  };

  const attach = async (orgId) => {
    const tid = attachSel[orgId];
    if (!tid) return;
    setErr("");
    try {
      await adminAttachBranch(orgId, tid);
      await loadBranches(orgId);
      await load();
    } catch (e) {
      setErr("Could not attach branch.");
    }
  };

  const detach = async (orgId, tid) => {
    setErr("");
    try {
      await adminDetachBranch(orgId, tid);
      await loadBranches(orgId);
      await load();
    } catch (e) {
      setErr("Could not detach branch.");
    }
  };

  const openEdit = (o) => {
    setEditId(o.id);
    setEditForm({ name: o.name, email: o.email, password: "", logo_url: o.logo_url || "", cover_image_url: o.cover_image_url || "", subscription_tier: o.subscription_tier || "hotel", merge_branch_items: !!o.merge_branch_items });
  };
  const uploadImg = async (kind, file) => {
    if (!file || !editId) return;
    setUploading(kind); setErr(""); setMsg("");
    try {
      const fn = kind === "cover" ? adminUploadOrgCover : adminUploadOrgLogo;
      const updated = await fn(editId, file);
      const key = kind === "cover" ? "cover_image_url" : "logo_url";
      setEditForm((f) => ({ ...f, [key]: (updated && updated[key]) || "" }));
      setMsg("Image uploaded.");
      await load();
    } catch (e) {
      setErr("Could not upload the image. Use a JPG or PNG under the size limit.");
    } finally {
      setUploading("");
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    setErr("");
    setMsg("");
    try {
      const payload = { name: editForm.name, email: editForm.email, logo_url: editForm.logo_url || "", cover_image_url: editForm.cover_image_url || "", subscription_tier: editForm.subscription_tier, merge_branch_items: !!editForm.merge_branch_items };
      if (editForm.password) payload.password = editForm.password;
      await adminUpdateOrg(editId, payload);
      setMsg(editForm.password ? "Saved. The owner password was reset." : "Group updated.");
      setEditId(null);
      await load();
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || "Could not update the group.");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>Groups (multi-branch owners)</h2>
      <p style={{ color: "#667", marginTop: 0 }}>
        Create a group for a multi-branch customer, attach their branches, and give the
        owner a single sign-in at <code>/owner/login</code> that shows all branches together.
      </p>

      {err && <div style={banner("#b00", "#fde8e8")}>{err}</div>}
      {msg && <div style={banner("#0a7", "#e7f7f0")}>{msg}</div>}

      <form onSubmit={create} style={card}>
        <h3 style={{ margin: "0 0 10px" }}>New group</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input placeholder="Group name (e.g. Kuriftu Group)" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...inp, flex: "1 1 220px" }} />
          <input placeholder="Owner email" type="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} style={{ ...inp, flex: "1 1 200px" }} />
          <input placeholder="Owner password (8+ chars)" type="text" value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })} style={{ ...inp, flex: "1 1 200px" }} />
          <button className="btn" disabled={creating} style={{ flex: "0 0 auto" }}>
            {creating ? "Creating…" : "Create group"}
          </button>
        </div>
      </form>

      <h3 style={{ marginBottom: 8 }}>Existing groups</h3>
      {loading ? (
        <p>Loading…</p>
      ) : orgs.length === 0 ? (
        <p style={{ color: "#667" }}>No groups yet. Create one above.</p>
      ) : (
        orgs.map((o) => (
          <div key={o.id} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <b>{o.name}</b>{" "}
                <span style={{ color: "#667" }}>· {o.branch_count} branch{o.branch_count === 1 ? "" : "es"} · owner: {o.email}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={() => (editId === o.id ? setEditId(null) : openEdit(o))}>
                  {editId === o.id ? "Cancel" : "Edit"}
                </button>
                <button className="btn" onClick={() => toggle(o.id)}>
                  {openId === o.id ? "Close" : "Manage branches"}
                </button>
              </div>
            </div>

            {editId === o.id && (
              <div style={{ marginTop: 12, borderTop: "1px solid #eef2f6", paddingTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                <label style={{ fontSize: 13, flex: "1 1 180px" }}>Group name
                  <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} style={{ ...inp, width: "100%", marginTop: 3 }} />
                </label>
                <label style={{ fontSize: 13, flex: "1 1 180px" }}>Owner email
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} style={{ ...inp, width: "100%", marginTop: 3 }} />
                </label>
                <label style={{ fontSize: 13, flex: "1 1 180px" }}>Plan (group tier)
                  <select value={editForm.subscription_tier} onChange={(e) => setEditForm({ ...editForm, subscription_tier: e.target.value })} style={{ ...inp, width: "100%", marginTop: 3 }}>
                    <option value="hotel">Hotel — single branch</option>
                    <option value="enterprise">Enterprise — all branches together</option>
                  </select>
                </label>
                <label style={{ fontSize: 13, flex: "1 1 100%", display: "flex", alignItems: "flex-start", gap: 8, marginTop: 2 }}>
                  <input type="checkbox" checked={!!editForm.merge_branch_items} onChange={(e) => setEditForm({ ...editForm, merge_branch_items: e.target.checked })} style={{ marginTop: 3 }} />
                  <span>Merge branch items for comparison
                    <div style={{ color: "#667", fontWeight: 400 }}>Default the owner's Branch-performance tab to a side-by-side table of the same dish across branches. Best when branches share a menu. The owner can still switch views.</div>
                  </span>
                </label>
                <label style={{ fontSize: 13, flex: "1 1 180px" }}>Group logo URL (optional)
                  <input value={editForm.logo_url} placeholder="https://…/logo.png" onChange={(e) => setEditForm({ ...editForm, logo_url: e.target.value })} style={{ ...inp, width: "100%", marginTop: 3 }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                    {editForm.logo_url ? <img src={resolveImageUrl(editForm.logo_url)} alt="" style={{ width: 34, height: 34, borderRadius: 7, objectFit: "cover", border: "1px solid #e3e3e3" }} /> : null}
                    <label className="btn secondary small" style={{ display: "inline-block", cursor: "pointer", margin: 0 }}>
                      {uploading === "logo" ? "Uploading…" : "Upload image"}
                      <input type="file" accept="image/*" style={{ display: "none" }} disabled={uploading === "logo"} onChange={(e) => { uploadImg("logo", e.target.files && e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                </label>
                <label style={{ display: "block", marginTop: 8 }}>Cover image URL (banner)
                  <input value={editForm.cover_image_url} placeholder="https://…/cover.jpg" onChange={(e) => setEditForm({ ...editForm, cover_image_url: e.target.value })} style={{ ...inp, width: "100%", marginTop: 3 }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                    {editForm.cover_image_url ? <img src={resolveImageUrl(editForm.cover_image_url)} alt="" style={{ width: 90, height: 34, borderRadius: 7, objectFit: "cover", border: "1px solid #e3e3e3" }} /> : null}
                    <label className="btn secondary small" style={{ display: "inline-block", cursor: "pointer", margin: 0 }}>
                      {uploading === "cover" ? "Uploading…" : "Upload image"}
                      <input type="file" accept="image/*" style={{ display: "none" }} disabled={uploading === "cover"} onChange={(e) => { uploadImg("cover", e.target.files && e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                </label>
                <label style={{ fontSize: 13, flex: "1 1 180px" }}>New password (leave blank to keep)
                  <input type="text" value={editForm.password} placeholder="8+ chars to reset" onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} style={{ ...inp, width: "100%", marginTop: 3 }} />
                </label>
                <button className="btn" onClick={saveEdit} disabled={savingEdit || !editForm.name || !editForm.email || (editForm.password && editForm.password.length < 8)}>
                  {savingEdit ? "Saving…" : "Save"}
                </button>
              </div>
            )}

            {openId === o.id && (
              <div style={{ marginTop: 12, borderTop: "1px solid #eef2f6", paddingTop: 12 }}>
                {branches[o.id] === undefined ? (
                  <p style={{ color: "#667" }}>Loading branches…</p>
                ) : branches[o.id].length === 0 ? (
                  <p style={{ color: "#667" }}>No branches attached yet.</p>
                ) : (
                  <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: 10 }}>
                    <tbody>
                      {branches[o.id].map((b) => (
                        <tr key={b.id}>
                          <td style={td}>{b.name}</td>
                          <td style={{ ...td, color: "#667", fontSize: 13 }}>{b.subscription_tier}</td>
                          <td style={{ ...td, textAlign: "right" }}>
                            <button className="btn" onClick={() => detach(o.id, b.id)}
                                    style={{ background: "#b00", color: "#fff", padding: "3px 10px" }}>
                              Detach
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <select value={attachSel[o.id] || ""}
                          onChange={(e) => setAttachSel((s) => ({ ...s, [o.id]: e.target.value }))}
                          style={{ ...inp, flex: "1 1 240px" }}>
                    <option value="">Attach a branch…</option>
                    {tenants.map((tn) => (
                      <option key={tn.id} value={tn.id}>{tn.name} (#{tn.id}) — {tn.email}</option>
                    ))}
                  </select>
                  <button className="btn" onClick={() => attach(o.id)} disabled={!attachSel[o.id]}>Attach</button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

const card = { background: "#fff", border: "1px solid #e3e8ef", borderRadius: 12, padding: 16, marginBottom: 14 };
const inp = { padding: "9px 10px", border: "1px solid #cdd8e3", borderRadius: 8, boxSizing: "border-box" };
const td = { padding: "6px 8px", borderBottom: "1px solid #f0f3f7" };
const banner = (fg, bg) => ({ color: fg, background: bg, border: `1px solid ${fg}22`, borderRadius: 8, padding: "8px 12px", marginBottom: 12 });
